// ===============================================================================
// src/auth/LoginScreen.jsx
// Email + password sign-in for PracticeOS.
//
// Oct 2026: "Black glass" redesign. True black ground, a split layout with a
// stationary brand scene on the left (headline + a fixed grid of product
// preview cards) and a glass sign-in card on the right with a soft cursor
// spotlight. Motion is limited to a one-time staggered entrance, a dim EKG
// trace along the bottom edge and the small live vitals trace. All motion is
// CSS, pauses when the tab is hidden, and is disabled under
// prefers-reduced-motion. The brand scene hides below 1040px wide.
// Replaces the May 2026 "Vital Sign" intro and the canvas neural mesh.
//
// May 12 2026: Forgot-password flow. Modes:
//   - "signin"    default email + password form
//   - "forgot"    email-only form to request a reset link
//   - "recovery"  two-password form to set a new password
//                 (mounted at /reset-password via App.jsx routing)
//   - "code" / "codeEnter" / "codePick"  patient sign in with a code
//
// Pre-authentication surface, so the global brand palette isn't loaded yet;
// uses local dark-theme constants (T) instead of token vars from ../lib/tokens.
// Oct 2026: accent colours are CSS variables (--pl-*) set on .pl-root from
// the last practice palette used on this device (src/lib/loginTint.js), so
// the screen carries the practice's brand tint; mint when none is known.
// After sign-in, AuthProvider mounts applyBrandPalette and the rest of the
// app reads --brand-* vars as usual.
//
// Oct 10 2026 polish (additive): Caps Lock warning, plain-language sign-in
// errors (src/lib/loginCore.js), opt-in "Remember my email on this device",
// "Welcome back to <practice>" once a staff sign-in happened on this device,
// and small motions: the card eases to each view's height with a crossfade,
// a check draws on success, one light sweep across Sign in when both fields
// are filled, the show-password slash draws, code digits settle in (staggered
// when Android fills the code), and a ring counts down to "Send a new code".
// ===============================================================================

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { supabase } from "../lib/supabaseClient";
import { consumeSignOutReason, clearSignOutReason } from "../lib/idleSignOut";
import { LangProvider, LANGS, useT, tk } from "../lib/i18n";
import { loginTintVars, rememberedLoginPractice } from "../lib/loginTint";
import { friendlyAuthError, AUTH_ERRORS, rememberedEmail, rememberEmail, resendSecondsLeft, resendWaitS } from "../lib/loginCore";

// ---------------------------------------------------------------------------
// Local dark-theme constants. Not pulled from C tokens because those target
// light surfaces and the global brand palette CSS vars aren't set until
// AuthProvider runs applyBrandPalette() post-sign-in.
// ---------------------------------------------------------------------------
const T = {
  bgBase:        "#000000",
  textPrimary:   "rgb(var(--pl-t))",
  textSecondary: "rgb(var(--pl-g1))",
  textTertiary:  "rgb(var(--pl-g2))",
  textFaint:     "rgb(var(--pl-g3))",
  textCardBody:  "rgb(var(--pl-t))",
  mint:          "rgb(var(--pl-a))",
  mintSoft:      "rgb(var(--pl-s2))",
  sky:           "#8FC3E8",
  skySoft:       "#A8D8F5",
  amber:         "#E8B467",
  lilac:         "#C3A6F0",
  onAccent:      "rgb(var(--pl-ink))",
  tile:          "linear-gradient(170deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))",
  tileBorder:    "rgba(255,255,255,0.08)",
  rowBg:         "rgba(255,255,255,0.03)",
  rowBorder:     "rgba(255,255,255,0.05)",
  cta:           "linear-gradient(180deg, rgb(var(--pl-c)) 0%, rgb(var(--pl-d)) 55%, rgb(var(--pl-d2)) 100%)",
  ctaBusy:       "linear-gradient(180deg, rgb(var(--pl-m)) 0%, rgb(var(--pl-d2)) 100%)",
  ghostBorder:   "rgba(255,255,255,0.11)",
  noticeBg:      "linear-gradient(135deg, rgba(208,138,46,0.14), rgba(208,138,46,0.05))",
  noticeBorder:  "rgba(232,170,90,0.28)",
  noticeIcon:    "#F2C27E",
  noticeText:    "#F4DDB6",
  successBg:     "linear-gradient(135deg, rgba(var(--pl-m),0.14), rgba(var(--pl-m),0.04))",
  successBorder: "rgba(var(--pl-a),0.30)",
  success:       "rgb(var(--pl-s))",
  red:           "#F2B3B3",
};

// Hero display sizes for the brand headline. The app type scale tops out at
// 48 (FONT_SIZE_SCALE); this one pre-auth headline deliberately sits above it.
const HERO_SIZE = 64;
const HERO_ACCENT_SIZE = 72;
const MONO  = "'JetBrains Mono', ui-monospace, monospace";
const SERIF = "'Instrument Serif', Georgia, serif";
const IDLE_MSG = "You were signed out after a period of inactivity to protect health information. Please sign in again.";

// ---------------------------------------------------------------------------
// Scoped CSS: keyframes, floating labels, focus rings and hover states that
// inline styles cannot express. Every selector is prefixed pl- so nothing
// leaks into the app shell after sign-in.
// ---------------------------------------------------------------------------
const LOGIN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500&display=swap');
.pl-rise{opacity:0;animation:pl-rise .9s cubic-bezier(.16,1,.3,1) forwards}
@keyframes pl-rise{from{opacity:0;transform:translateY(16px);filter:blur(6px)}to{opacity:1;transform:none;filter:blur(0)}}
.pl-draw{stroke-dasharray:140;stroke-dashoffset:140;animation:pl-draw 1.6s cubic-bezier(.65,0,.35,1) .2s forwards}
@keyframes pl-draw{to{stroke-dashoffset:0}}
.pl-ecg{stroke-dasharray:180 1400;animation:pl-ecg 5.5s linear infinite}
@keyframes pl-ecg{from{stroke-dashoffset:1580}to{stroke-dashoffset:0}}
.pl-ecg2{stroke-dasharray:90 400;animation:pl-ecg2 2.6s linear infinite}
@keyframes pl-ecg2{from{stroke-dashoffset:490}to{stroke-dashoffset:0}}
.pl-pulse{animation:pl-pulse 2.4s ease-out infinite}
@keyframes pl-pulse{0%{box-shadow:0 0 0 0 rgba(var(--pl-m),.55)}100%{box-shadow:0 0 0 10px rgba(var(--pl-m),0)}}
.pl-slide{animation:pl-slide .5s cubic-bezier(.16,1,.3,1)}
@keyframes pl-slide{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.pl-shake-a{animation:pl-shA .45s cubic-bezier(.36,.07,.19,.97)}
.pl-shake-b{animation:pl-shB .45s cubic-bezier(.36,.07,.19,.97)}
@keyframes pl-shA{20%,80%{transform:translateX(3px)}30%,50%,70%{transform:translateX(-7px)}40%,60%{transform:translateX(7px)}}
@keyframes pl-shB{20%,80%{transform:translateX(3px)}30%,50%,70%{transform:translateX(-7px)}40%,60%{transform:translateX(7px)}}
.pl-spin{width:16px;height:16px;border-radius:50%;border:2px solid rgba(var(--pl-ink),.25);border-top-color:rgb(var(--pl-ink));animation:pl-sp .8s linear infinite}
@keyframes pl-sp{to{transform:rotate(360deg)}}
.pl-field{position:relative}
.pl-field input{width:100%;box-sizing:border-box;height:56px;padding:23px 16px 7px;border-radius:12px;border:1px solid rgba(255,255,255,.09);background:rgba(var(--pl-k),.55);color:rgb(var(--pl-t));font:500 15px 'DM Sans',system-ui,sans-serif;outline:none;transition:border-color .25s,box-shadow .25s,background .25s}
.pl-field input:hover{border-color:rgba(255,255,255,.17)}
.pl-field input:focus{border-color:rgba(var(--pl-a),.8);background:rgba(var(--pl-k2),.6);box-shadow:0 0 0 4px rgba(var(--pl-m),.12),0 0 24px -4px rgba(var(--pl-m),.35)}
.pl-field input.pl-bad{border-color:#E58A8A;box-shadow:0 0 0 4px rgba(229,138,138,.12)}
.pl-field label{position:absolute;left:17px;top:18px;font-size:14px;color:rgb(var(--pl-g2));pointer-events:none;transition:all .2s cubic-bezier(.16,1,.3,1)}
.pl-field input:focus + label,.pl-field input:not(:placeholder-shown) + label{top:8px;font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:rgb(var(--pl-a))}
.pl-field input.pl-code{text-align:center;font:600 22px 'JetBrains Mono',ui-monospace,monospace;letter-spacing:.4em;padding-top:20px}
.pl-cta{position:relative;overflow:hidden;transition:transform .2s cubic-bezier(.16,1,.3,1),box-shadow .3s}
.pl-cta:hover:not(:disabled){transform:translateY(-2px);box-shadow:inset 0 1px 0 rgba(255,255,255,.45),0 18px 40px -12px rgba(var(--pl-m),.7)}
.pl-cta:active:not(:disabled){transform:translateY(0) scale(.99)}
.pl-ghost{transition:background .2s,border-color .2s,color .2s}
.pl-ghost:hover:not(:disabled){background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.2)}
.pl-link:hover{color:rgb(var(--pl-s))}
.pl-cta:focus-visible,.pl-ghost:focus-visible,.pl-link:focus-visible,.pl-seg:focus-visible{outline:2px solid rgb(var(--pl-s));outline-offset:3px}
.pl-swap{animation:pl-swap .38s cubic-bezier(.16,1,.3,1)}
@keyframes pl-swap{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.pl-hgt{transition:height .42s cubic-bezier(.16,1,.3,1)}
.pl-done{opacity:.86;transform:scale(.985);transition:opacity .45s ease,transform .45s cubic-bezier(.16,1,.3,1)}
.pl-check path{stroke-dasharray:24;stroke-dashoffset:24;animation:pl-draw .45s cubic-bezier(.65,0,.35,1) forwards}
.pl-sheen{position:absolute;inset:0;pointer-events:none;background:linear-gradient(105deg,transparent 30%,rgba(255,255,255,.42) 50%,transparent 70%);transform:translateX(-120%);animation:pl-sheen 1.1s cubic-bezier(.4,0,.2,1) .1s forwards}
@keyframes pl-sheen{to{transform:translateX(120%)}}
.pl-pop{animation:pl-pop .32s cubic-bezier(.34,1.56,.64,1) both}
@keyframes pl-pop{from{transform:translateY(4px) scale(.9);opacity:.3}to{transform:none;opacity:1}}
.pl-eye-slash{transition:stroke-dashoffset .3s cubic-bezier(.65,0,.35,1)}
.pl-ring-fg{animation:pl-ring linear forwards}
@keyframes pl-ring{from{stroke-dashoffset:0}to{stroke-dashoffset:44}}
.pl-check-row input{accent-color:rgb(var(--pl-a));width:16px;height:16px;margin:0;cursor:pointer}
.pl-paused *{animation-play-state:paused!important}
@media (prefers-reduced-motion:reduce){.pl-root *{animation-duration:.01ms!important;animation-iteration-count:1!important}.pl-rise{opacity:1}.pl-hgt,.pl-done,.pl-eye-slash{transition:none!important}.pl-done{transform:none}.pl-sheen{display:none}}
`;

// ---------------------------------------------------------------------------
// Small inline icons (stroke SVG, currentColor).
// ---------------------------------------------------------------------------
function Icon({ d, size = 16, width = 2, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={style}>
      <path d={d} />
    </svg>
  );
}
const ICONS = {
  clock:   "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2",
  close:   "M6 6l12 12M18 6L6 18",
  alert:   "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v5M12 16.5v.01",
  check:   "M5 12.5l4.5 4.5L19 7.5",
  arrow:   "M5 12h14M13 6l6 6-6 6",
  eye:     "M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  eyeOff:  "M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 5.1A9.8 9.8 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-3.2 4.1M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 19 12 19c1.6 0 3-.4 4.3-1",
  person:  "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5",
  back:    "M19 12H5M11 6l-6 6 6 6",
  lock:    "M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4",
  shield:  "M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9 12l2 2 4-4",
  up:      "M7 17L17 7M9 7h8v8",
};

// Show / hide password: the slash draws in and out instead of swapping icons.
function EyeIcon({ off }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
      <path d="M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
      <path className="pl-eye-slash" d="M4 4l16 16" strokeDasharray="23" strokeDashoffset={off ? 0 : 23} />
    </svg>
  );
}

function LogoMark({ size = 44, animate = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="38" height="38" rx="12" style={{ fill: "rgba(var(--pl-a),0.06)", stroke: "rgba(var(--pl-a),0.35)" }} />
      <path className={animate ? "pl-draw" : undefined} d="M14 29V11h7.5a5.5 5.5 0 0 1 0 11H14"
            style={{ stroke: T.mint }} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ===============================================================================
// BrandScene - left half. Headline plus a fixed grid of decorative product
// preview tiles. Purely presentational (aria-hidden on the tiles); no data.
// ===============================================================================
const tileStyle = {
  padding: "18px 20px", borderRadius: 20, background: T.tile,
  border: "1px solid " + T.tileBorder, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
};
const tileLabel = (color) => ({ font: "500 10px " + MONO, color, letterSpacing: "0.1em", textTransform: "uppercase" });

// Counts the HEDIS tile from 5 up to 45 once, easing out, after the tiles land.
// Jumps straight to the end value under prefers-reduced-motion.
function useCountUp(from, to, duration, delay) {
  const [value, setValue] = useState(from);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setValue(to); return; }
    let raf = 0;
    let start = 0;
    const step = (now) => {
      if (!start) start = now;
      const k = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setValue(Math.round(from + (to - from) * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(step); }, delay);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [from, to, duration, delay]);
  return value;
}

function BrandScene({ t }) {
  const gapsClosed = useCountUp(5, 45, 2400, 1000);
  const h = new Date().getHours();
  const greeting = h < 12 ? t("Good morning") : h < 18 ? t("Good afternoon") : t("Good evening");
  const appts = [
    { time: "8:30 AM",  type: t("Annual exam"),     color: T.mint,  dot: T.mint },
    { time: "9:15 AM",  type: t("Follow-up"),       color: T.amber, dot: "rgba(255,255,255,0.25)" },
    { time: "10:00 AM", type: t("Telehealth"),      color: T.sky,   dot: "rgba(255,255,255,0.25)" },
    { time: "11:30 AM", type: t("Care management"), color: T.lilac, dot: "rgba(255,255,255,0.25)" },
  ];
  const ecg = "M0 24 H60 l8 -6 l6 6 h14 l6 -18 l8 32 l7 -24 l5 10 h28 l8 -5 l8 5 H220";
  const dotSep = <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgb(var(--pl-g4))" }} />;

  return (
    <section style={{
      position: "relative", display: "flex", flexDirection: "column", justifyContent: "space-between",
      padding: "52px 48px 48px 64px", boxSizing: "border-box", minHeight: "100vh",
    }}>
      <div className="pl-rise" style={{ display: "flex", alignItems: "center", gap: 14, animationDelay: "80ms" }}>
        <LogoMark animate />
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em", color: T.textPrimary }}>{/* i18n-exempt: product name */}PracticeOS</span>
          <span style={{ font: "500 10.5px " + MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textFaint }}>{t("by Immaculate Consulting")}</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 44, maxWidth: 640 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div className="pl-rise" style={{
            display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 10,
            padding: "7px 14px 7px 10px", borderRadius: 999,
            background: "rgba(var(--pl-a),0.06)", border: "1px solid rgba(var(--pl-a),0.2)", animationDelay: "180ms",
          }}>
            <span className="pl-pulse" style={{ width: 7, height: 7, borderRadius: "50%", background: T.mint }} />
            <span style={{ font: "500 11px " + MONO, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgb(var(--pl-s))" }}>{greeting}</span>
          </div>
          <h1 className="pl-rise" style={{
            margin: 0, fontSize: HERO_SIZE, lineHeight: 1.02, fontWeight: 600, letterSpacing: "-0.035em",
            color: T.textPrimary, animationDelay: "260ms",
          }}>
            {t("Care runs")}<br />
            <span style={{
              fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, letterSpacing: "-0.01em", fontSize: HERO_ACCENT_SIZE,
              background: "linear-gradient(100deg, rgb(var(--pl-s2)) 0%, rgb(var(--pl-a)) 45%, #6FB3E8 100%)",
              WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
            }}>{t("smoother here.")}</span>
          </h1>
          <p className="pl-rise" style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: T.textSecondary, maxWidth: 460, animationDelay: "360ms" }}>
            {t("Scheduling, charting, care management and billing for independent and AMH practices, in one calm workspace.")}
          </p>
        </div>

        <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
          <div className="pl-rise" style={{ ...tileStyle, padding: 20, gridRow: "span 2", animationDelay: "480ms" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{t("Today")}</span>
              <span style={tileLabel(T.mint)}>{t("Schedule")}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {appts.map((a) => (
                <div key={a.time} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: 12,
                  background: T.rowBg, border: "1px solid " + T.rowBorder,
                }}>
                  <span style={{ width: 3, height: 28, borderRadius: 2, background: a.color }} />
                  <span style={{ font: "500 11px " + MONO, color: "rgb(var(--pl-g2))", width: 60 }}>{a.time}</span>
                  <span style={{ fontSize: 13, color: T.textCardBody, flexGrow: 1 }}>{a.type}</span>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: a.dot }} />
                </div>
              ))}
            </div>
          </div>

          <div className="pl-rise" style={{ ...tileStyle, animationDelay: "560ms" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={tileLabel(T.sky)}>{t("Vitals")}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textTertiary }}>
                <span className="pl-pulse" style={{ width: 6, height: 6, borderRadius: "50%", background: T.mint }} />{t("Live")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 10 }}>
              <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", color: T.textPrimary }}>
                {/* i18n-exempt: sample reading */}118<span style={{ color: "rgb(var(--pl-g3))", fontWeight: 400 }}>/</span>76
              </span>
              <span style={{ fontSize: 12, color: T.textTertiary }}>{/* i18n-exempt: unit */}mmHg</span>
            </div>
            <svg viewBox="0 0 220 44" style={{ width: "100%", height: 44, marginTop: 6 }}>
              <path d={ecg} fill="none" stroke="rgba(143,195,232,0.16)" strokeWidth="1.5" />
              <path className="pl-ecg2" d={ecg} fill="none" stroke={T.skySoft} strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 5px rgba(143,195,232,0.9))" }} />
            </svg>
          </div>

          <div className="pl-rise" style={{ ...tileStyle, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 8, animationDelay: "640ms" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={tileLabel(T.mint)}>{/* i18n-exempt: measure set name */}HEDIS</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.mint }}>
                <Icon d={ICONS.up} size={12} width={2.4} />{t("This month")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.02em", color: T.textPrimary, fontVariantNumeric: "tabular-nums", minWidth: 40 }}>{gapsClosed}</span>
              <span style={{ fontSize: 13, color: T.textCardBody, lineHeight: 1.35 }}>{t("Care gaps closed")}</span>
            </div>
          </div>

          <div className="pl-rise" style={{ gridColumn: "span 2", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14, animationDelay: "720ms" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 16, background: "rgba(var(--pl-a),0.06)", border: "1px solid rgba(var(--pl-a),0.22)" }}>
              <span style={{ width: 24, height: 24, flexShrink: 0, borderRadius: "50%", background: "rgb(var(--pl-d))", color: T.onAccent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon d={ICONS.check} size={12} width={3.4} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{t("Claim ready to bill")}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 16, background: "rgba(143,195,232,0.06)", border: "1px solid rgba(143,195,232,0.22)" }}>
              <span style={{ width: 24, height: 24, flexShrink: 0, borderRadius: "50%", background: "rgba(143,195,232,0.18)", color: T.skySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon d={ICONS.shield} size={13} width={2.2} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: "#DCEBF3" }}>{t("Eligibility verified")}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pl-rise" style={{
        display: "flex", alignItems: "center", gap: 20, font: "500 10.5px " + MONO, letterSpacing: "0.1em",
        textTransform: "uppercase", color: "rgb(var(--pl-g3))", animationDelay: "700ms",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Icon d={ICONS.lock} size={13} />{t("Encrypted")}</span>
        {dotSep}
        <span>{t("Role-based access")}</span>
        {dotSep}
        <span>{t("Auto sign-out")}</span>
      </div>
    </section>
  );
}

// ===============================================================================
// Language switch - segmented EN / ES with a sliding indicator. Same
// LangProvider state as the shared LanguageSwitch, restyled for this card.
// ===============================================================================
function LangSegment() {
  const { lang, setLang, t } = useT();
  const idx = Math.max(0, LANGS.findIndex((l) => l.code === lang));
  return (
    <div role="group" aria-label={t("Language")} style={{
      position: "relative", display: "flex", padding: 3, borderRadius: 999, flexShrink: 0,
      background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.08)",
    }}>
      <span aria-hidden="true" style={{
        position: "absolute", top: 3, left: 3, width: 44, height: 32, borderRadius: 999, boxSizing: "border-box",
        background: "linear-gradient(180deg, rgba(var(--pl-a),0.28), rgba(var(--pl-a),0.12))",
        border: "1px solid rgba(var(--pl-a),0.4)",
        transform: "translateX(" + (idx * 44) + "px)", transition: "transform .45s cubic-bezier(.16,1,.3,1)",
      }} />
      {LANGS.map((l) => (
        <button key={l.code} type="button" lang={l.code} className="pl-seg"
                aria-pressed={l.code === lang} aria-label={l.label} onClick={() => setLang(l.code)}
                style={{
                  position: "relative", width: 44, height: 32, border: 0, background: "transparent",
                  color: T.textPrimary, font: "600 11.5px 'DM Sans', sans-serif", cursor: "pointer", borderRadius: 999,
                }}>{l.code.toUpperCase()}</button>
      ))}
    </div>
  );
}

// ===============================================================================
// Field - floating-label input with an optional show/hide password toggle.
// ===============================================================================
function Field({ id, label, type = "text", value, onChange, autoComplete, autoFocus, inputMode, pattern, bad, code, reveal, onCaps }) {
  const { t } = useT();
  const [shown, setShown] = useState(false);
  const isPw = type === "password";
  return (
    <div className="pl-field">
      <input
        id={id}
        type={isPw && shown ? "text" : type}
        placeholder=" "
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        inputMode={inputMode}
        pattern={pattern}
        aria-invalid={bad ? true : undefined}
        onKeyDown={onCaps ? (e) => { if (e.getModifierState) onCaps(e.getModifierState("CapsLock")); } : undefined}
        onKeyUp={onCaps ? (e) => { if (e.getModifierState) onCaps(e.getModifierState("CapsLock")); } : undefined}
        onBlur={onCaps ? () => onCaps(false) : undefined}
        className={[bad ? "pl-bad" : "", code ? "pl-code" : ""].join(" ").trim() || undefined}
        style={isPw && reveal ? { paddingRight: 56 } : undefined}
      />
      <label htmlFor={id}>{label}</label>
      {isPw && reveal && (
        <button type="button" className="pl-ghost" onClick={() => setShown((s) => !s)}
                aria-label={shown ? t("Hide password") : t("Show password")}
                style={{
                  position: "absolute", right: 6, top: 6, width: 44, height: 44, border: "1px solid transparent",
                  borderRadius: 10, background: "transparent", color: T.textSecondary, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
          <EyeIcon off={shown} />
        </button>
      )}
    </div>
  );
}

function CapsNote({ t }) {
  return (
    <div className="pl-slide" role="status" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: -6, fontSize: 12, color: T.amber }}>
      <Icon d={ICONS.up} size={13} width={2.4} />
      <span>{t("Caps Lock is on")}</span>
    </div>
  );
}

// ===============================================================================
// LoginScreen
//
// Renders every mode from the same component:
//   - signin    default form
//   - forgot    email-only request form (transitions in place from signin)
//   - recovery  new-password form (mounted via /reset-password route)
//   - code / codeEnter / codePick   patient sign in with a code
//
// Patients and staff share this screen, so it runs inside the portal's
// LangProvider (same storage key) with an English / Spanish switch.
// Messages are stored as English keys and translated at render so they
// follow the switch; server text (e.message) is shown as is.
// ===============================================================================
export default function LoginScreen(props) {
  return (
    <LangProvider storageKey="pos-lang-portal" defaultLang="auto">
      <LoginScreenBody {...props} />
    </LangProvider>
  );
}

function LoginScreenBody({ recoveryMode = false }) {
  const { signIn, error } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();

  // Form fields - reused across modes where applicable
  // Opt-in "Remember my email on this device" (email only, never the password).
  const [initialEmail] = useState(() => rememberedEmail());
  const [email,    setEmail]    = useState(initialEmail);
  const [remember, setRemember] = useState(!!initialEmail);
  const [caps,     setCaps]     = useState(false);
  const [done,     setDone]     = useState(false);
  const [practiceName] = useState(() => rememberedLoginPractice());
  const [password, setPassword] = useState("");
  const [newPw1,   setNewPw1]   = useState("");
  const [newPw2,   setNewPw2]   = useState("");
  const [busy,     setBusy]     = useState(false);
  const [localErr, setLocalErr] = useState("");
  // After an automatic logoff, say why rather than showing a bare sign-in.
  const [info,     setInfo]     = useState(() =>
    consumeSignOutReason() === "idle" ? tk(IDLE_MSG) : "");

  // Sign in with a code (patients). codeReq is the request id from
  // portal-otp-request; choices is the account list for a shared phone/email.
  const [codeChannel, setCodeChannel] = useState("sms");
  const [codeDest,    setCodeDest]    = useState("");
  const [codeReq,     setCodeReq]     = useState(null);
  const [code,        setCode]        = useState("");
  const [choices,     setChoices]     = useState([]);
  const [codeSentAt,  setCodeSentAt]  = useState(null);
  const [resendLeft,  setResendLeft]  = useState(0);

  // Mode state machine. recoveryMode prop wins on initial mount.
  // Modes: signin | forgot | recovery | code (ask for phone/email) |
  //        codeEnter (type the code) | codePick (choose an account)
  const [mode, setMode] = useState(recoveryMode ? "recovery" : "signin");

  // In recovery mode, watch for the auth event that fires after Supabase
  // processes the hash from the email link. PASSWORD_RECOVERY indicates a
  // temporary session was established; this gates the submit button so
  // expired or malformed links surface a clear error instead of failing
  // silently inside updateUser.
  const [hasRecoverySession, setHasRecoverySession] = useState(false);

  // Layout + motion state.
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? !window.matchMedia("(max-width: 1040px)").matches : true);
  const [hidden, setHidden] = useState(false);
  // Read once per mount; the remembered palette only changes after sign-in.
  const [tintVars] = useState(() => loginTintVars());
  const [shake, setShake] = useState(0);
  const cardRef = useRef(null);
  const spotRaf = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 1040px)");
    const onChange = () => setWide(!mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", onChange); else mq.removeListener(onChange); };
  }, []);

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); cancelAnimationFrame(spotRaf.current); };
  }, []);

  useEffect(() => {
    if (mode !== "recovery") return;
    // Fast path: if Supabase already processed the hash before mount.
    // supabase-best-effort: getSession reads the local session; a missing session is handled just below
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) setHasRecoverySession(true);
    });
    // Slow path: subscribe in case the event fires post-mount.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setHasRecoverySession(true);
      }
    });
    return () => sub?.subscription?.unsubscribe();
  }, [mode]);

  // Clear messages when switching modes - otherwise a stale error from
  // signin would carry into forgot or vice versa. Skipped on first mount so
  // an initial message (the automatic sign-out note) is not wiped at once.
  const modeMountedRef = useRef(false);
  useEffect(() => {
    if (!modeMountedRef.current) { modeMountedRef.current = true; return; }
    setLocalErr("");
    setInfo("");
  }, [mode]);

  async function handleSignIn() {
    if (!email || !password) { setLocalErr(tk("Enter email and password.")); return; }
    setBusy(true);
    setLocalErr("");
    try {
      await signIn(email, password);
      rememberEmail(email, remember);
      clearSignOutReason();
      setDone(true);
    } catch (e) {
      setLocalErr(plainError(e));
      setBusy(false);
    }
  }

  async function handleSendReset() {
    if (!email.trim()) { setLocalErr(tk("Enter your email address.")); return; }
    setBusy(true);
    setLocalErr("");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) throw error;
      setInfo(tk("Check your email for a reset link. It may take a minute to arrive."));
    } catch (e) {
      setLocalErr(e.message || tk("Could not send reset email. Try again or contact your administrator."));
    } finally {
      setBusy(false);
    }
  }

  async function handleSetNewPassword() {
    if (!newPw1 || !newPw2) { setLocalErr(tk("Enter your new password in both fields.")); return; }
    if (newPw1 !== newPw2)  { setLocalErr(tk("Passwords do not match.")); return; }
    if (newPw1.length < 8)  { setLocalErr(tk("Password must be at least 8 characters.")); return; }
    if (!hasRecoverySession) {
      setLocalErr(tk("This reset link has expired or is invalid. Request a new one from the sign-in page."));
      return;
    }
    setBusy(true);
    setLocalErr("");
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw1 });
      if (error) throw error;
      // Password updated and session is now valid - clear the recovery
      // hash so a refresh doesn't try to re-process it, then bounce to
      // dashboard. AuthProvider already has the session from the SIGNED_IN
      // event, so the dashboard mounts directly.
      window.history.replaceState(null, "", "/");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setLocalErr(e.message || tk("Could not update password. Try again or request a new reset link."));
      setBusy(false);
    }
  }

  // ---- Sign in with a code ------------------------------------------------
  // Calls one of the portal-otp-* edge functions. Returns the JSON body for
  // both success and error replies.
  async function callOtp(slug, body) {
    const { data, error: fnErr } = await supabase.functions.invoke(slug, { body });
    if (!fnErr) return data || {};
    try { return (await fnErr.context.json()) || { error: "server_error" }; } catch { return { error: "server_error" }; }
  }

  async function handleSendCode() {
    const raw = codeDest.trim();
    if (codeChannel === "sms") {
      let d = raw.replace(/\D/g, "");
      if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
      if (d.length !== 10) { setLocalErr(tk("Enter a 10-digit mobile number.")); return; }
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      setLocalErr(tk("Enter a valid email address.")); return;
    }
    setBusy(true);
    setLocalErr("");
    setInfo("");
    try {
      const r = await callOtp("portal-otp-request", { channel: codeChannel, destination: raw, lang });
      if (r.ok && r.request_id) {
        setCodeReq(r.request_id);
        setCodeSentAt(Date.now());
        setCode("");
        setChoices([]);
        if (mode === "codeEnter") setInfo(tk("A new code is on its way."));
        else setMode("codeEnter");
      } else if (r.error === "rate_limited") {
        setLocalErr(tk("Too many codes were requested. Please wait 15 minutes and try again."));
      } else if (r.error === "invalid_destination") {
        setLocalErr(codeChannel === "sms" ? tk("Enter a 10-digit mobile number.") : tk("Enter a valid email address."));
      } else {
        setLocalErr(tk("Could not send a code. Please try again."));
      }
    } catch {
      setLocalErr(tk("Could not send a code. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode(userId) {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) { setLocalErr(tk("Enter the 6-digit code.")); return; }
    setBusy(true);
    setLocalErr("");
    setInfo("");
    try {
      const r = await callOtp("portal-otp-verify", { request_id: codeReq, code: digits, user_id: userId || undefined });
      if (r.choose && r.choose.length) {
        setChoices(r.choose);
        setMode("codePick");
        setBusy(false);
        return;
      }
      if (r.ok && r.token_hash) {
        const { error: vErr } = await supabase.auth.verifyOtp({ token_hash: r.token_hash, type: "magiclink" });
        if (vErr) throw vErr;
        clearSignOutReason();
        setDone(true);
        // AuthProvider picks up the new session (SIGNED_IN) and routes on.
        return;
      }
      if (r.error === "invalid" && typeof r.tries_left === "number" && r.tries_left > 0) {
        setLocalErr({
          key: r.tries_left === 1 ? tk("That code is not right. 1 try left.") : tk("That code is not right. {n} tries left."),
          vars: { n: r.tries_left },
        });
      } else if (r.error === "locked" || r.error === "invalid") {
        setLocalErr(tk("Too many wrong tries. Send a new code."));
      } else if (r.error === "expired") {
        setLocalErr(tk("That code has expired. Send a new code."));
      } else {
        setLocalErr(tk("Could not sign in. Please try again."));
      }
      if (mode === "codePick") setMode("codeEnter");
    } catch {
      setLocalErr(tk("Could not sign in. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  function startCodeFlow() {
    setCodeReq(null);
    setCode("");
    setChoices([]);
    setMode("code");
  }

  // Android: offer to fill the code from the text message (WebOTP). The text
  // ends with "@<host> #<code>" for this.
  useEffect(() => {
    if (mode !== "codeEnter" || codeChannel !== "sms" || typeof window === "undefined" || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    navigator.credentials.get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((otp) => { if (otp && otp.code) setCode(String(otp.code).slice(0, 6)); })
      .catch(() => { /* dismissed or unsupported; typing still works */ });
    return () => ac.abort();
  }, [mode, codeChannel, codeReq]);

  // Plain sentence for known sign-in errors; unknown server text as before.
  function plainError(raw) {
    const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
    const k = friendlyAuthError(raw, online);
    if (k !== AUTH_ERRORS.generic) return k;
    return (raw && (raw.message || (typeof raw === "string" ? raw : ""))) || tk("Sign in failed.");
  }

  // A sign-in that succeeded but was then refused by the app (for example a
  // deactivated account) comes back as an auth error: undo the finish state.
  useEffect(() => {
    if (error && done) { setDone(false); setBusy(false); }
  }, [error, done]);

  // "Send a new code" waits RESEND_WAIT_S seconds after each send.
  useEffect(() => {
    if (!codeSentAt) { setResendLeft(0); return; }
    const waitS = resendWaitS();
    setResendLeft(resendSecondsLeft(codeSentAt, Date.now(), waitS));
    const id = setInterval(() => {
      const left = resendSecondsLeft(codeSentAt, Date.now(), waitS);
      setResendLeft(left);
      if (left <= 0) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [codeSentAt]);

  function submit() {
    if (mode === "signin")    handleSignIn();
    if (mode === "forgot")    handleSendReset();
    if (mode === "recovery")  handleSetNewPassword();
    if (mode === "code")      handleSendCode();
    if (mode === "codeEnter") handleVerifyCode();
  }

  function onKeyDown(e) {
    if (e.key !== "Enter") return;
    submit();
  }

  // localErr and info hold English keys (or server text, which t() leaves as
  // is), or { key, vars } for a message with a number in it.
  const tMsg = (m) => (m && typeof m === "object" ? t(m.key, m.vars) : t(m));
  const message = localErr ? tMsg(localErr) : (error ? tMsg(plainError(error)) : null);
  const isIdleNotice = info === IDLE_MSG;

  // Shake the card each time a new error appears.
  useEffect(() => { if (message) setShake((n) => n + 1); }, [message]);

  // Cursor spotlight on the card, written straight to CSS vars (no re-render).
  function onCardMove(e) {
    const el = cardRef.current;
    if (!el || spotRaf.current) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    spotRaf.current = requestAnimationFrame(() => {
      spotRaf.current = 0;
      el.style.setProperty("--pl-mx", ((x / r.width) * 100).toFixed(1) + "%");
      el.style.setProperty("--pl-my", ((y / r.height) * 100).toFixed(1) + "%");
    });
  }

  // -------------------------------------------------------------------------
  // Mode-specific card content
  // -------------------------------------------------------------------------
  const headerByMode = {
    signin:    { title: t("Welcome back"), sub: practiceName ? t("Sign in to {name}", { name: practiceName }) : t("Sign in to your practice") },
    forgot:    { title: t("Reset your password"),  sub: t("We'll send you a reset link") },
    recovery:  { title: t("Set a new password"),   sub: t("Enter and confirm your new password") },
    code:      { title: t("Sign in with a code"),  sub: t("We'll text or email you a 6-digit code") },
    codeEnter: { title: t("Enter your code"),      sub: t("If it matches our records, a code is on its way. It expires in 10 minutes.") },
    codePick:  { title: t("Choose an account"),    sub: t("More than one person uses this phone or email. Who is signing in?") },
  };
  const header = headerByMode[mode] || headerByMode.signin;

  const ctaLabel =
    busy && mode === "signin"    ? t("Signing in...")         :
    busy && mode === "forgot"    ? t("Sending...")            :
    busy && mode === "recovery"  ? t("Updating password...")  :
    busy && mode === "code"      ? t("Sending...")            :
    busy && mode === "codeEnter" ? t("Signing in...")         :
    mode === "signin"            ? t("Sign in")               :
    mode === "forgot"            ? t("Send reset link")       :
    mode === "code"              ? t("Send code")             :
    mode === "codeEnter"         ? t("Sign in")               :
                                   t("Set new password");
  const ctaDisabled = busy || (mode === "recovery" && !hasRecoverySession);
  const bad = !!message;
  const digits = code.split("");
  // Code cells: a pasted or auto-filled code lands one digit after another.
  const prevDigits = useRef(0);
  const digitFrom = useRef(0);
  if (digits.length !== prevDigits.current) {
    digitFrom.current = digits.length - prevDigits.current > 1 ? prevDigits.current : -1;
    prevDigits.current = digits.length;
  }

  // One light sweep across Sign in each time both fields become filled.
  const ready = mode === "signin" && !!email && !!password && !busy;
  const [sheen, setSheen] = useState(0);
  const wasReady = useRef(false);
  useEffect(() => {
    if (ready && !wasReady.current) setSheen((n) => n + 1);
    wasReady.current = ready;
  }, [ready]);

  // The card eases to each view's height (measured from its content).
  const innerRef = useRef(null);
  const [cardH, setCardH] = useState(null);
  const [hgtReady, setHgtReady] = useState(false);
  const padY = wide ? 38 + 32 : 30 + 26;
  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setCardH(el.offsetHeight + padY);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const tm = setTimeout(() => setHgtReady(true), 50);
    return () => { ro.disconnect(); clearTimeout(tm); };
  }, [padY]);

  const linkStyle = {
    background: "transparent", border: "none", color: T.mint, fontSize: 13, fontWeight: 500,
    cursor: "pointer", padding: "4px 2px", fontFamily: "inherit", textDecoration: "none", minHeight: 32,
  };
  const ghostBtn = {
    width: "100%", height: 50, borderRadius: 12, border: "1px solid " + T.ghostBorder,
    background: "rgba(255,255,255,0.025)", color: T.textCardBody, font: "500 14px 'DM Sans', sans-serif",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
  };

  return (
    <div className={"pl-root" + (hidden ? " pl-paused" : "")} style={{
      // Accent colours from the last practice palette used on this device
      // (src/lib/loginTint.js); the original mint when none is remembered.
      ...tintVars,
      minHeight: "100vh",
      background: T.bgBase,
      color: T.textPrimary,
      fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <style>{LOGIN_CSS}</style>

      {/* Ambient: a faint glow behind the card and a dim EKG trace along the bottom. */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{
          position: "absolute", right: wide ? "8%" : "50%", top: "50%", width: 760, height: 760,
          transform: wide ? "translateY(-50%)" : "translate(50%, -50%)", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(var(--pl-d2),0.14) 0%, rgba(var(--pl-d2),0) 60%)",
        }} />
        <svg viewBox="0 0 1600 200" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "6vh", width: "100%", height: 120, opacity: 0.55 }}>
          <path d="M0 100 H520 l18 -10 l14 10 h40 l14 -78 l18 150 l16 -110 l12 38 h60 l20 -16 l18 16 H1600"
                fill="none" stroke="rgba(143,195,232,0.12)" strokeWidth="1.2" />
          <path className="pl-ecg" d="M0 100 H520 l18 -10 l14 10 h40 l14 -78 l18 150 l16 -110 l12 38 h60 l20 -16 l18 16 H1600"
                fill="none" stroke={T.skySoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 6px rgba(143,195,232,0.9)) drop-shadow(0 0 18px rgba(143,195,232,0.5))" }} />
        </svg>
      </div>

      <div style={{
        position: "relative", zIndex: 1, minHeight: "100vh", display: "grid",
        gridTemplateColumns: wide ? "minmax(0, 1.25fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
      }}>
        {wide && <BrandScene t={t} />}

        <main style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: wide ? "40px 24px" : "32px 16px", boxSizing: "border-box", gap: 22,
        }}>
          {!wide && (
            <div className="pl-rise" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <LogoMark size={36} />
              <span style={{ fontSize: 18, fontWeight: 700 }}>{/* i18n-exempt: product name */}PracticeOS</span>
            </div>
          )}

          <div className="pl-rise" style={{ width: "100%", maxWidth: 448, animationDelay: "200ms" }}>
            <div className={[shake === 0 ? "" : (shake % 2 ? "pl-shake-a" : "pl-shake-b"), done ? "pl-done" : ""].join(" ").trim() || undefined}
                 style={{ position: "relative", borderRadius: 20 }}>
              <div style={{
                position: "relative", padding: 1, borderRadius: 20,
                background: "linear-gradient(170deg, rgba(255,255,255,0.16), rgba(255,255,255,0.05) 40%, rgba(var(--pl-a),0.10))",
              }}>
                <div ref={cardRef} onMouseMove={onCardMove} className={hgtReady ? "pl-hgt" : undefined} style={{
                  position: "relative", overflow: "hidden", boxSizing: "border-box",
                  height: cardH || undefined,
                  padding: wide ? "38px 38px 32px" : "30px 22px 26px", borderRadius: 20,
                  background: "radial-gradient(520px circle at var(--pl-mx, 50%) var(--pl-my, 0%), rgba(var(--pl-a),0.10), transparent 45%), linear-gradient(170deg, rgba(var(--pl-k2),0.88) 0%, rgba(var(--pl-k),0.94) 100%)",
                  backdropFilter: "blur(28px) saturate(160%)",
                  WebkitBackdropFilter: "blur(28px) saturate(160%)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 50px 120px -40px rgba(0,0,0,0.9)",
                }}>
                  <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, top: 0, height: 140, background: "linear-gradient(180deg, rgba(255,255,255,0.05), transparent)", pointerEvents: "none" }} />
                  <div ref={innerRef}>

                  {/* Header */}
                  <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 26, gap: 12 }}>
                    <div key={mode} className="pl-swap" style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                      <span style={{ font: "500 10.5px " + MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.mint }}>{/* i18n-exempt: product name */}PracticeOS</span>
                      <h2 style={{ margin: 0, fontSize: wide ? 28 : 24, fontWeight: 600, letterSpacing: "-0.025em", lineHeight: 1.15, color: T.textPrimary }}>{header.title}</h2>
                      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: T.textSecondary }}>{header.sub}</p>
                    </div>
                    <LangSegment />
                  </div>

                  {/* Automatic sign-out notice (amber) */}
                  {isIdleNotice && (
                    <div className="pl-slide" role="status" style={{
                      position: "relative", display: "flex", gap: 12, alignItems: "flex-start",
                      padding: "13px 12px 13px 14px", marginBottom: 20, borderRadius: 12,
                      background: T.noticeBg, border: "1px solid " + T.noticeBorder,
                    }}>
                      <span style={{ color: T.noticeIcon, flexShrink: 0, marginTop: 1 }}><Icon d={ICONS.clock} size={18} /></span>
                      <span style={{ flexGrow: 1, fontSize: 13, lineHeight: 1.55, color: T.noticeText }}>{t(IDLE_MSG)}</span>
                      <button type="button" className="pl-ghost" onClick={() => setInfo("")} aria-label={t("Dismiss")} style={{
                        width: 32, height: 32, flexShrink: 0, border: "1px solid transparent", borderRadius: 8,
                        background: "transparent", color: T.noticeText, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}><Icon d={ICONS.close} size={13} width={2.4} /></button>
                    </div>
                  )}

                  {/* Mode-specific form body */}
                  <div key={mode} className="pl-swap" onKeyDown={onKeyDown} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 14 }}>
                    {mode === "signin" && (
                      <>
                        <Field id="pl-email" label={t("Email")} type="email" value={email}
                               onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus={!initialEmail} bad={bad} />
                        <Field id="pl-password" label={t("Password")} type="password" value={password}
                               onChange={e => setPassword(e.target.value)} autoComplete="current-password" autoFocus={!!initialEmail}
                               bad={bad} reveal onCaps={setCaps} />
                        {caps && <CapsNote t={t} />}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: -4 }}>
                          <label className="pl-check-row" style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 32, fontSize: 13, color: T.textSecondary, cursor: "pointer" }}>
                            <input type="checkbox" checked={remember} data-testid="pl-remember"
                                   onChange={(e) => { setRemember(e.target.checked); if (!e.target.checked) rememberEmail("", false); }} />
                            <span>{t("Remember my email on this device")}</span>
                          </label>
                          <button type="button" className="pl-link" onClick={() => setMode("forgot")} style={linkStyle}>
                            {t("Forgot password?")}
                          </button>
                        </div>
                      </>
                    )}

                    {mode === "code" && (
                      <>
                        <div role="radiogroup" aria-label={t("How should we send your code?")} style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                          {[["sms", t("Text me")], ["email", t("Email me")]].map(([v, label]) => {
                            const on = codeChannel === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                className="pl-ghost"
                                onClick={() => { setCodeChannel(v); setCodeDest(""); setLocalErr(""); }}
                                style={{
                                  height: 46, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                  background: on ? "rgba(var(--pl-a),0.12)" : "rgba(255,255,255,0.025)",
                                  color: on ? T.mintSoft : T.textSecondary,
                                  border: "1px solid " + (on ? "rgba(var(--pl-a),0.45)" : T.ghostBorder),
                                }}
                              >{label}</button>
                            );
                          })}
                        </div>
                        <Field id="pl-code-dest" label={codeChannel === "sms" ? t("Mobile phone") : t("Email")}
                               type={codeChannel === "sms" ? "tel" : "email"}
                               inputMode={codeChannel === "sms" ? "tel" : "email"}
                               autoComplete={codeChannel === "sms" ? "tel-national" : "email"}
                               value={codeDest} onChange={e => setCodeDest(e.target.value)} autoFocus bad={bad} />
                        <div style={{ fontSize: 13, color: T.textTertiary, marginTop: -4 }}>
                          {t("Use the mobile number or email your practice has on file.")}
                        </div>
                      </>
                    )}

                    {mode === "codeEnter" && (
                      <>
                        <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 8 }}>
                          {[0, 1, 2, 3, 4, 5].map((i) => {
                            const filled = i < digits.length;
                            const active = i === digits.length;
                            return (
                              <div key={i + (filled ? "f" : "e")} className={filled ? "pl-pop" : undefined} style={{
                                animationDelay: filled && digitFrom.current >= 0 && i >= digitFrom.current ? ((i - digitFrom.current) * 70) + "ms" : undefined,
                                height: 56, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
                                font: "500 24px " + MONO, color: T.textPrimary, background: "rgba(var(--pl-k),0.55)",
                                border: "1px solid " + (filled ? "rgba(var(--pl-a),0.55)" : active ? "rgba(var(--pl-a),0.35)" : "rgba(255,255,255,0.09)"),
                                boxShadow: filled ? "0 0 18px -6px rgba(var(--pl-m),0.7)" : "none", transition: "all .25s",
                              }}>{filled ? digits[i] : ""}</div>
                            );
                          })}
                        </div>
                        {/* No maxLength: a pasted "123 456" or "Code: 123456" still works. */}
                        <Field id="pl-code-input" label={t("6-digit code")} type="text" inputMode="numeric"
                               autoComplete="one-time-code" pattern="[0-9]*" value={code}
                               onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus bad={bad} code />
                      </>
                    )}

                    {mode === "codePick" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {choices.map((c) => (
                          <button
                            key={c.user_id}
                            type="button"
                            disabled={busy}
                            className="pl-ghost"
                            onClick={() => handleVerifyCode(c.user_id)}
                            style={{
                              ...ghostBtn, justifyContent: "space-between", padding: "0 16px", height: 54,
                              fontWeight: 600, textAlign: "left", cursor: busy ? "default" : "pointer",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <span style={{ color: T.mint }}><Icon d={ICONS.person} size={16} width={1.9} /></span>
                              {c.label}
                            </span>
                            <span style={{ color: T.textTertiary }}><Icon d={ICONS.arrow} size={15} /></span>
                          </button>
                        ))}
                      </div>
                    )}

                    {mode === "forgot" && (
                      <>
                        <Field id="pl-forgot-email" label={t("Email")} type="email" value={email}
                               onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus bad={bad} />
                        <div style={{ fontSize: 13, color: T.textTertiary, lineHeight: 1.5, marginTop: -4 }}>
                          {t("Enter the email you sign in with. We'll send a reset link if an account exists.")}
                        </div>
                      </>
                    )}

                    {mode === "recovery" && (
                      <>
                        <Field id="pl-new-pw1" label={t("New password")} type="password" value={newPw1}
                               onChange={e => setNewPw1(e.target.value)} autoComplete="new-password" autoFocus bad={bad} reveal onCaps={setCaps} />
                        <Field id="pl-new-pw2" label={t("Confirm new password")} type="password" value={newPw2}
                               onChange={e => setNewPw2(e.target.value)} autoComplete="new-password" bad={bad} reveal onCaps={setCaps} />
                        {caps && <CapsNote t={t} />}
                        <div style={{ fontSize: 13, color: T.textTertiary, marginTop: -4 }}>
                          {hasRecoverySession ? t("At least 8 characters") : t("Verifying your reset link...")}
                        </div>
                      </>
                    )}

                    {/* Inline status messages */}
                    <div aria-live="polite" style={{ marginTop: (message || (info && !isIdleNotice)) ? 0 : -14 }}>
                      {message && (
                        <div className="pl-slide" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.5, color: T.red }}>
                          <span style={{ flexShrink: 0, marginTop: 1 }}><Icon d={ICONS.alert} size={16} /></span>
                          <span>{message}</span>
                        </div>
                      )}
                      {info && !isIdleNotice && (
                        <div className="pl-slide" style={{
                          display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 12,
                          background: T.successBg, border: "1px solid " + T.successBorder, color: T.success, fontSize: 13, lineHeight: 1.5,
                        }}>
                          <span style={{ flexShrink: 0, marginTop: 1 }}><Icon d={ICONS.check} size={16} width={2.4} /></span>
                          <span>{tMsg(info)}</span>
                        </div>
                      )}
                    </div>

                    {/* Primary action button */}
                    {mode !== "codePick" && (
                      <button
                        type="button"
                        className="pl-cta"
                        onClick={submit}
                        disabled={ctaDisabled}
                        style={{
                          height: 56, marginTop: 2, border: 0, borderRadius: 12,
                          background: busy ? T.ctaBusy : T.cta, color: T.onAccent,
                          font: "700 15.5px 'DM Sans', sans-serif", letterSpacing: "0.005em",
                          cursor: ctaDisabled ? "default" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 0 rgba(0,0,0,0.12), 0 12px 30px -14px rgba(var(--pl-m),0.8)",
                          opacity: (mode === "recovery" && !hasRecoverySession) ? 0.5 : 1,
                        }}
                      >
                        {sheen > 0 && ready && <span key={sheen} className="pl-sheen" aria-hidden="true" />}
                        {done ? (
                          <svg className="pl-check" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d={ICONS.check} />
                          </svg>
                        ) : busy && <span className="pl-spin" aria-hidden="true" />}
                        <span>{done ? t("Signed in") : ctaLabel}</span>
                        {!busy && !done && <Icon d={ICONS.arrow} size={16} width={2.3} />}
                      </button>
                    )}
                  </div>

                  {/* Patients: sign in with a code instead of a password */}
                  {mode === "signin" && (
                    <>
                      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 14, margin: "24px 0 16px" }}>
                        <span style={{ height: 1, flexGrow: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12))" }} />
                        <span style={{ font: "500 10.5px " + MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textFaint }}>{t("or")}</span>
                        <span style={{ height: 1, flexGrow: 1, background: "linear-gradient(90deg, rgba(255,255,255,0.12), transparent)" }} />
                      </div>
                      <button type="button" className="pl-ghost" onClick={startCodeFlow} style={{ ...ghostBtn, position: "relative" }}>
                        <Icon d={ICONS.person} size={16} width={1.9} />
                        <span>{t("Patient? Sign in with a code instead")}</span>
                      </button>
                    </>
                  )}

                  {/* Code flow: resend / change destination */}
                  {(mode === "codeEnter" || mode === "codePick") && (
                    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginTop: 16 }}>
                      {mode === "codeEnter" && (
                        <button type="button" className="pl-link" onClick={handleSendCode} disabled={busy || resendLeft > 0}
                                style={{ ...linkStyle, display: "flex", alignItems: "center", gap: 8, color: resendLeft > 0 ? T.textTertiary : T.mint, cursor: resendLeft > 0 ? "default" : "pointer" }}>
                          {resendLeft > 0 && (
                            <svg width={16} height={16} viewBox="0 0 18 18" aria-hidden="true">
                              <circle cx="9" cy="9" r="7" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="2" />
                              <circle key={codeSentAt} className="pl-ring-fg" cx="9" cy="9" r="7" fill="none" stroke="rgb(var(--pl-a))" strokeWidth="2"
                                      strokeDasharray="44" strokeLinecap="round" transform="rotate(-90 9 9)"
                                      style={{ animationDuration: resendWaitS() + "s" }} />
                            </svg>
                          )}
                          {resendLeft > 0 ? t("Send a new code in {n}s", { n: resendLeft }) : t("Send a new code")}
                        </button>
                      )}
                      <button type="button" className="pl-link" onClick={startCodeFlow} style={linkStyle}>
                        {t("Use a different phone or email")}
                      </button>
                    </div>
                  )}

                  {/* Secondary action: back to sign in */}
                  {(mode === "forgot" || mode === "code" || mode === "codeEnter" || mode === "codePick") && (
                    <div style={{ position: "relative", display: "flex", justifyContent: "center", marginTop: 10 }}>
                      <button type="button" className="pl-link" onClick={() => setMode("signin")}
                              style={{ ...linkStyle, display: "flex", alignItems: "center", gap: 6, color: T.textSecondary }}>
                        <Icon d={ICONS.back} size={14} />
                        {t("Back to sign in")}
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="pl-rise" style={{ margin: 0, maxWidth: 448, textAlign: "center", fontSize: 13, color: T.textFaint, animationDelay: "420ms" }}>
            {t("Need access? Contact your practice administrator.")}
          </p>
        </main>
      </div>
    </div>
  );
}
